import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageContainer from '../../../components/PageContainer/PageContainer';
import { useAuth } from '../../../../../auth/AuthProvider';
import { parseHashParams } from '../../../utils/parseHashParams';
import { RouteEnum } from '../../../constantes/RouteEnum';
import { useGetAccountSchema } from '../../../services/SchemaService';
import { useSchemaLoader } from '../../../hooks/useSchemaLoader';

type CallbackError = 'missing-token' | 'login-failed' | 'schema-unavailable';

const ERROR_MESSAGES: Record<CallbackError, string> = {
  'missing-token': 'Não conseguimos identificar suas credenciais. Por favor, faça login novamente.',
  'login-failed': 'Não foi possível concluir o login. Tente novamente.',
  'schema-unavailable': 'Não foi possível carregar as configurações da conta. Tente novamente.',
};

export const Callback: React.FC = () => {
  const navigate = useNavigate();
  const { loginWithCallback } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<CallbackError | null>(null);
  const hasProcessedRef = useRef(false);

  // Dispara o fetch do schema da conta (efeito colateral: popula o localStorage)
  useGetAccountSchema();
  const { waitForSchema } = useSchemaLoader();

  useEffect(() => {
    if (hasProcessedRef.current) return;
    hasProcessedRef.current = true;

    const handleCallback = async () => {
      const { access_token, expires_in } = parseHashParams(window.location.hash);

      if (!access_token) {
        setError('missing-token');
        setLoading(false);
        return;
      }

      const schema = await waitForSchema();
      if (!schema) {
        setError('schema-unavailable');
        setLoading(false);
        return;
      }

      try {
        await loginWithCallback(access_token, expires_in);
        window.history.replaceState(null, '', window.location.pathname);
        navigate(RouteEnum.HOME);
      } catch (err) {
        console.error('Erro ao processar callback de autenticação:', err);
        setError('login-failed');
        setLoading(false);
      }
    };

    handleCallback();
  }, [loginWithCallback, navigate, waitForSchema]);

  const handleBackToLogin = () => {
    navigate(RouteEnum.LOGIN_USERNAME);
  };

  if (error) {
    return (
      <PageContainer
        title="Erro na autenticação"
        thereIsButton
        buttonText="Voltar ao login"
        onClick={handleBackToLogin}
      >
        <p>{ERROR_MESSAGES[error]}</p>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title="Autenticando..."
      thereIsButton
      loading={loading}
      disabled
      buttonText="Aguarde"
    >
      <p>Estamos validando suas credenciais.</p>
    </PageContainer>
  );
};

export default Callback;
